import { DateTime, Settings } from "luxon";

/**
 * Helper utilities for working with Luxon DateTime objects
 * This provides a centralized place for common date/time operations
 */

/**
 * Set the default timezone for Luxon
 * Should be called on app initialization
 * @param {string} timezone - IANA timezone string (e.g., 'America/New_York')
 */
export const setDefaultTimezone = (timezone) => {
  Settings.defaultZone = timezone;
};

/**
 * Set the default locale for Luxon
 * Should be called on app initialization
 * @param {string} locale - Locale string (e.g., 'en', 'da', 'de')
 */
export const setDefaultLocale = (locale) => {
  Settings.defaultLocale = locale;
};

/**
 * Parse a string with a specific format
 * @param {string} input - The date/time string to parse
 * @param {string} format - The format string (using Luxon tokens)
 * @returns {DateTime} Luxon DateTime object
 */
export const parseFormat = (input, format) => {
  return DateTime.fromFormat(input, format);
};

/**
 * Get current DateTime
 * @returns {DateTime} Current DateTime in the default timezone
 */
export const now = () => {
  return DateTime.now();
};

/**
 * Parse an ISO string
 * @param {string} iso - ISO 8601 string
 * @returns {DateTime} Luxon DateTime object
 */
export const fromISO = (iso) => {
  return DateTime.fromISO(iso);
};

/**
 * Parse a JavaScript Date object
 * @param {Date} jsDate - JavaScript Date object
 * @returns {DateTime} Luxon DateTime object
 */
export const fromJSDate = (jsDate) => {
  return DateTime.fromJSDate(jsDate);
};

/**
 * Create a DateTime from an object
 * @param {Object} obj - Object with date/time properties
 * @returns {DateTime} Luxon DateTime object
 */
export const fromObject = (obj) => {
  return DateTime.fromObject(obj);
};

/**
 * Get the long date format for the current locale (Luxon format)
 * Similar to moment's longDateFormat('LL')
 * @returns {string} Long date format string using Luxon tokens
 */
export const getLongDateFormat = () => {
  // Luxon uses localized formatting automatically with DateTime.DATE_FULL
  // For consistency with bootstrap datetimepicker, we'll use a custom format
  const locale = Settings.defaultLocale || 'en';
  
  // Common formats for different locales (using Luxon format tokens)
  const formats = {
    'en': 'MMMM d, yyyy',
    'da': 'd. MMMM yyyy',
    'de': 'd. MMMM yyyy',
    'fo': 'd. MMMM yyyy',
  };
  
  return formats[locale] || 'MMMM d, yyyy';
};

/**
 * Get the long date format for Bootstrap Datepicker (moment.js format)
 * Bootstrap Datepicker still uses moment.js internally, so it needs moment format tokens
 * @returns {string} Long date format string using moment.js tokens
 */
export const getMomentLongDateFormat = () => {
  const locale = Settings.defaultLocale || 'en';
  
  // Common formats for different locales (using moment.js format tokens)
  // Note: moment uses 'YYYY' for 4-digit year, Luxon uses 'yyyy'
  // Note: moment uses 'D' for day of month, 'DD' for zero-padded
  const formats = {
    'en': 'MMMM D, YYYY',
    'da': 'D. MMMM YYYY',
    'de': 'D. MMMM YYYY',
    'fo': 'D. MMMM YYYY',
  };
  
  return formats[locale] || 'MMMM D, YYYY';
};

/**
 * Get the current locale
 * @returns {string} Current locale
 */
export const getLocale = () => {
  return Settings.defaultLocale || 'en';
};

/**
 * Get the minimum of multiple DateTimes
 * @param {...DateTime} dates - Variable number of DateTime objects
 * @returns {DateTime} The earliest DateTime
 */
export const min = (...dates) => {
  return DateTime.min(...dates);
};

/**
 * Get the maximum of multiple DateTimes
 * @param {...DateTime} dates - Variable number of DateTime objects
 * @returns {DateTime} The latest DateTime
 */
export const max = (...dates) => {
  return DateTime.max(...dates);
};
